import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';
import { ipApi } from '../../services/api';

// ─── Static Data ─────────────────────────────────────────────────────────────

const CATEGORIES = ['All','Consumables','IV Fluids','Medicines','Sutures','Antiseptics','Respiratory'];
const DEPARTMENTS = ['ICU','OT','Pharmacy','Wards','Emergency','Lab','Radiology'];

// ─── Reference date (as given in task) ────────────────────────────────────────
const TODAY = new Date();

// ─── Helper Functions ─────────────────────────────────────────────────────────

function daysUntil(dateStr) {
  const d = new Date(dateStr);
  return Math.floor((d - TODAY) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}

function formatCurrency(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

function stockStatus(current, min) {
  const c = Number(current) || 0;
  const m = Number(min) || 0;
  if (c <= 0) return 'Critical';
  if (m > 0 && c <= m) return 'Low';
  return 'OK';
}

function applyGrnToStock(items, grnRows) {
  const list = [...(items || [])];
  (grnRows || []).forEach((row) => {
    const name = String(row.name || '').trim();
    const qty = Number(row.qty) || 0;
    if (!name || qty <= 0) return;
    const idx = list.findIndex((i) => i.name.toLowerCase() === name.toLowerCase());
    if (idx >= 0) {
      const item = { ...list[idx] };
      item.current = (Number(item.current) || 0) + qty;
      if (row.batch) item.batch = row.batch;
      if (row.mrp) item.mrp = Number(row.mrp) || item.mrp;
      if (row.expiry) item.expiry = row.expiry;
      item.status = stockStatus(item.current, item.min);
      list[idx] = item;
    } else {
      const min = 10;
      list.push({
        id: `STK-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        name,
        category: 'Consumables',
        unit: row.unit || 'pcs',
        batch: row.batch || '',
        current: qty,
        min,
        status: stockStatus(qty, min),
        expiry: row.expiry || '',
        mrp: Number(row.mrp) || 0,
      });
    }
  });
  return list;
}

function applyIndentToStock(items, indentRows) {
  const list = [...(items || [])];
  const errors = [];
  (indentRows || []).forEach((row) => {
    const name = String(row.name || '').trim();
    const issued = Number(row.issued) || Number(row.requested) || 0;
    if (!name || issued <= 0) return;
    const idx = list.findIndex((i) => i.name.toLowerCase() === name.toLowerCase());
    if (idx < 0) {
      errors.push(`"${name}" not found in stock`);
      return;
    }
    const item = { ...list[idx] };
    const next = (Number(item.current) || 0) - issued;
    if (next < 0) {
      errors.push(`Insufficient "${name}" (have ${item.current}, need ${issued})`);
      return;
    }
    item.current = next;
    item.status = stockStatus(item.current, item.min);
    list[idx] = item;
  });
  return { items: list, errors };
}

function exportStockCsv(items) {
  const headers = ['Item Name', 'Category', 'Batch', 'Unit', 'Current Stock', 'Min Level', 'Status', 'Expiry', 'MRP'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = (items || []).map((i) => [
    i.name, i.category, i.batch, i.unit, i.current, i.min, i.status, i.expiry, i.mrp,
  ].map(esc).join(','));
  const csv = [headers.map(esc).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory-stock-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function getRowStyle(item) {
  const days = daysUntil(item.expiry);
  if (days < 0)  return { background:'#7f1d1d', color:'#fff' };
  if (item.status === 'Critical') return { background:'#fee2e2' };
  if (item.status === 'Low')      return { background:'#fef9c3' };
  return {};
}

function StatusBadge({ status, expiry }) {
  const days = daysUntil(expiry);
  const isExpired = days < 0;
  const label = isExpired ? 'Expired' : status;
  const styles = {
    display:'inline-block', padding:'2px 10px', borderRadius:20,
    fontWeight:600, fontSize:11, letterSpacing:'0.04em',
    ...(label==='OK'       && { background:'#dcfce7', color:'#166534' }),
    ...(label==='Low'      && { background:'#fef08a', color:'#854d0e' }),
    ...(label==='Critical' && { background:'#fee2e2', color:'#991b1b' }),
    ...(label==='Expired'  && { background:'#7f1d1d', color:'#fff'    }),
  };
  return <span style={styles}>{label}</span>;
}

function POStatusBadge({ status }) {
  const styles = {
    display:'inline-block', padding:'2px 10px', borderRadius:20,
    fontWeight:600, fontSize:11, letterSpacing:'0.04em',
    ...(status==='Pending'  && { background:'#fef08a', color:'#854d0e' }),
    ...(status==='Approved' && { background:'#dbeafe', color:'#1e40af' }),
    ...(status==='Received' && { background:'#dcfce7', color:'#166534' }),
  };
  return <span style={styles}>{status}</span>;
}

// ─── Inline Styles ─────────────────────────────────────────────────────────────
const S = {
  page:       { padding:'24px 0' },
  pageHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 },
  title:      { margin:0, fontSize:22, fontWeight:700 },
  subtitle:   { margin:'2px 0 0', fontSize:13, opacity:0.6 },

  tabBar:     { display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid #e5e7eb', paddingBottom:0 },
  tab:        { padding:'8px 18px', border:'none', background:'transparent', cursor:'pointer',
                fontWeight:500, fontSize:14, borderBottom:'2px solid transparent', marginBottom:-2,
                borderRadius:'4px 4px 0 0', transition:'all 0.15s' },
  tabActive:  { borderBottom:'2px solid #2563eb', color:'#2563eb', background:'#eff6ff', fontWeight:600 },

  statGrid:   { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 },
  statCard:   { background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'16px 20px',
                boxShadow:'0 1px 3px rgba(0,0,0,0.06)' },
  statValue:  { fontSize:28, fontWeight:700, lineHeight:1.2, margin:0 },
  statLabel:  { fontSize:12, color:'#6b7280', marginTop:4, fontWeight:500 },

  toolbar:    { display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' },
  searchBox:  { flex:1, minWidth:200, padding:'7px 12px', border:'1px solid #d1d5db',
                borderRadius:7, fontSize:13, outline:'none' },
  catBtn:     { padding:'5px 12px', border:'1px solid #d1d5db', borderRadius:20,
                background:'#fff', cursor:'pointer', fontSize:12, fontWeight:500, transition:'all 0.15s' },
  catBtnActive:{ background:'#2563eb', color:'#fff', border:'1px solid #2563eb' },

  table:      { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th:         { padding:'9px 10px', background:'#f8fafc', borderBottom:'2px solid #e5e7eb',
                fontWeight:600, textAlign:'left', fontSize:12, color:'#374151', whiteSpace:'nowrap' },
  td:         { padding:'8px 10px', borderBottom:'1px solid #f0f0f0', verticalAlign:'middle' },

  sectionTitle:{ fontSize:15, fontWeight:700, margin:'24px 0 12px',
                 borderLeft:'3px solid #2563eb', paddingLeft:10 },

  formGrid:   { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:18 },
  formGrid2:  { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:18 },
  label:      { display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 },
  inputField: { width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6,
                fontSize:13, outline:'none', boxSizing:'border-box' },

  addRowBtn:  { display:'inline-flex', alignItems:'center', gap:6, marginTop:6, marginBottom:14,
                padding:'6px 14px', border:'1px dashed #2563eb', borderRadius:6,
                background:'#eff6ff', color:'#2563eb', cursor:'pointer', fontSize:13, fontWeight:500 },

  removeBtn:  { background:'#fee2e2', border:'none', color:'#991b1b', borderRadius:4,
                padding:'2px 8px', cursor:'pointer', fontWeight:700, fontSize:14 },

  totalRow:   { background:'#f0fdf4', fontWeight:700 },

  histCard:   { background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8,
                padding:'14px 18px', marginBottom:14 },
  histTitle:  { fontWeight:700, fontSize:13, marginBottom:6, color:'#1e3a5f' },
  histMeta:   { fontSize:12, color:'#6b7280', marginBottom:8 },

  periodBtn:  { padding:'5px 14px', border:'1px solid #d1d5db', borderRadius:20,
                background:'#fff', cursor:'pointer', fontSize:12, fontWeight:500 },
  periodBtnA: { background:'#2563eb', color:'#fff', border:'1px solid #2563eb' },

  tfoot:      { fontWeight:700, background:'#f0fdf4' },
  infoBanner: { background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8,
                padding:'10px 16px', marginBottom:18, fontSize:13, color:'#1e40af' },

  saveBtn:    { marginTop:6, padding:'9px 24px', background:'#2563eb', color:'#fff',
                border:'none', borderRadius:7, fontWeight:600, cursor:'pointer', fontSize:14 },
  secBtn:     { marginTop:6, padding:'9px 20px', background:'#fff', color:'#374151',
                border:'1px solid #d1d5db', borderRadius:7, fontWeight:600, cursor:'pointer', fontSize:14, marginLeft:8 },
  smBtn:      { padding:'4px 10px', fontSize:12, fontWeight:600, border:'1px solid #d1d5db',
                borderRadius:5, background:'#fff', cursor:'pointer' },
  smBtnBlue:  { padding:'4px 10px', fontSize:12, fontWeight:600, border:'1px solid #2563eb',
                borderRadius:5, background:'#eff6ff', color:'#2563eb', cursor:'pointer' },
};

const UNITS = ['Box', 'Bottle', 'Pcs', 'Strip', 'Vial', 'Pack', 'Roll', 'Kg', 'Ltr'];

const EMPTY_ITEM = () => ({
  id: `STK-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
  name: '',
  category: 'Consumables',
  unit: 'Box',
  batch: '',
  current: 0,
  min: 10,
  mrp: 0,
  expiry: '',
  status: 'Critical',
});

function normalizeItem(item) {
  const current = Number(item.current) || 0;
  const min = Number(item.min) || 0;
  return {
    ...item,
    current,
    min,
    mrp: Number(item.mrp) || 0,
    status: stockStatus(current, min),
  };
}

function ItemFormModal({ item, title, onSave, onClose, busy, error }) {
  const [form, setForm] = useState(() => normalizeItem(item || EMPTY_ITEM()));
  const set = (key, val) => setForm((f) => normalizeItem({ ...f, [key]: val }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(normalizeItem(form));
  };

  return (
    <div className="q-modal-backdrop" onClick={onClose}>
      <div className="q-modal card" style={{ maxWidth: 560, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        {error && <div className="error mb">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="field full">
              <label>Item Name *</label>
              <input className="input" required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Surgical Gloves (M)" />
            </div>
            <div className="field">
              <label>Category</label>
              <select className="input" value={form.category} onChange={(e) => set('category', e.target.value)}>
                {CATEGORIES.filter((c) => c !== 'All').map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Unit</label>
              <select className="input" value={form.unit} onChange={(e) => set('unit', e.target.value)}>
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Current Stock</label>
              <input className="input" type="number" min={0} value={form.current} onChange={(e) => set('current', e.target.value)} />
            </div>
            <div className="field">
              <label>Min Level</label>
              <input className="input" type="number" min={0} value={form.min} onChange={(e) => set('min', e.target.value)} />
            </div>
            <div className="field">
              <label>MRP ₹</label>
              <input className="input" type="number" min={0} value={form.mrp} onChange={(e) => set('mrp', e.target.value)} />
            </div>
            <div className="field">
              <label>Batch No.</label>
              <input className="input" value={form.batch} onChange={(e) => set('batch', e.target.value)} placeholder="BT2024A" />
            </div>
            <div className="field">
              <label>Expiry Date</label>
              <input className="input" type="date" value={form.expiry ? form.expiry.slice(0, 10) : ''} onChange={(e) => set('expiry', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save Item'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ItemViewModal({ item, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="q-modal-backdrop" onClick={onClose}>
      <div className="q-modal card" style={{ maxWidth: 480, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{item.name}</h3>
        <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 12px', fontSize: 13, margin: 0 }}>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Category</dt><dd style={{ margin: 0 }}>{item.category}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Unit</dt><dd style={{ margin: 0 }}>{item.unit}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Current Stock</dt><dd style={{ margin: 0, fontWeight: 700 }}>{item.current}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Min Level</dt><dd style={{ margin: 0 }}>{item.min}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Status</dt><dd style={{ margin: 0 }}><StatusBadge status={item.status} expiry={item.expiry} /></dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Batch</dt><dd style={{ margin: 0 }}>{item.batch || '—'}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>Expiry</dt><dd style={{ margin: 0 }}>{formatDate(item.expiry)}</dd>
          <dt style={{ color: '#64748b', fontWeight: 600 }}>MRP</dt><dd style={{ margin: 0 }}>{formatCurrency(item.mrp)}</dd>
        </dl>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" onClick={() => { onClose(); onEdit(item); }}>Edit</button>
        </div>
      </div>
    </div>
  );
}

function StockOverviewTab({ stockItems, onSaveStock, saving, saveError, onClearError, onRequestAdd }) {
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState('All');
  const [items, setItems] = useState(stockItems);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [itemBusy, setItemBusy] = useState(false);
  const [itemErr, setItemErr] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => { setItems(stockItems); }, [stockItems]);

  useEffect(() => {
    if (onRequestAdd) onRequestAdd.current = () => setEditItem(EMPTY_ITEM());
  }, [onRequestAdd]);

  const stats = useMemo(() => {
    const low      = items.filter(i => i.status === 'Low').length;
    const critical = items.filter(i => i.status === 'Critical').length;
    const expSoon  = items.filter(i => { const d = daysUntil(i.expiry); return d >= 0 && d <= 90; }).length;
    return { total: items.length, low, critical, expSoon };
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(item => {
      const matchCat  = category === 'All' || item.category === category;
      const matchSearch = !search ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        (item.batch || '').toLowerCase().includes(search.toLowerCase()) ||
        item.category.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [search, category, items]);

  const updateQty = (id, val) => {
    setItems((list) => list.map((i) => {
      if (i.id !== id) return i;
      const current = Math.max(0, Number(val) || 0);
      return { ...i, current, status: stockStatus(current, i.min) };
    }));
  };

  const persistItems = async (nextItems, successMsg) => {
    setItemBusy(true);
    setItemErr('');
    onClearError?.();
    try {
      await onSaveStock(nextItems.map(normalizeItem));
      setItems(nextItems.map(normalizeItem));
      setSaveMsg(successMsg);
      setTimeout(() => setSaveMsg(''), 3000);
      return true;
    } catch (e) {
      setItemErr(e.message || 'Could not save item');
      return false;
    } finally {
      setItemBusy(false);
    }
  };

  const saveNewOrEditedItem = async (formItem) => {
    const dup = items.some((i) => i.id !== formItem.id && i.name.toLowerCase() === formItem.name.trim().toLowerCase());
    if (dup) {
      setItemErr('An item with this name already exists.');
      return;
    }
    const next = editItem?.id && items.some((i) => i.id === editItem.id)
      ? items.map((i) => (i.id === formItem.id ? formItem : i))
      : [...items, formItem];
    const ok = await persistItems(next, editItem?.name ? 'Item updated.' : 'Item added to inventory.');
    if (ok) setEditItem(null);
  };

  const handleSaveLevels = async () => {
    onClearError?.();
    setSaveMsg('');
    try {
      await onSaveStock(items.map(normalizeItem));
      setSaveMsg('Stock levels saved.');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      setItemErr(e.message || 'Could not save stock levels');
    }
  };

  return (
    <div>
      {(saveError || itemErr) && <div className="error mb">{saveError || itemErr}</div>}
      {saveMsg && <div className="info-banner mb">{saveMsg}</div>}
      {/* Stat Cards */}
      <div style={S.statGrid}>
        <div style={S.statCard}>
          <p style={{ ...S.statValue, color:'#1e3a5f' }}>{stats.total}</p>
          <p style={S.statLabel}>Total Items</p>
        </div>
        <div style={S.statCard}>
          <p style={{ ...S.statValue, color:'#854d0e' }}>{stats.low}</p>
          <p style={S.statLabel}>Low Stock</p>
        </div>
        <div style={S.statCard}>
          <p style={{ ...S.statValue, color:'#991b1b' }}>{stats.critical}</p>
          <p style={S.statLabel}>Critical Stock</p>
        </div>
        <div style={S.statCard}>
          <p style={{ ...S.statValue, color:'#6d28d9' }}>{stats.expSoon}</p>
          <p style={S.statLabel}>Expiring Soon (90d)</p>
        </div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input
          style={S.searchBox}
          placeholder="Search by item name, batch, category…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div style={{ ...S.toolbar, marginBottom:16 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            style={{ ...S.catBtn, ...(category === cat ? S.catBtnActive : {}) }}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ overflowX:'auto', padding:0 }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['#','Item Name','Category','Unit','Current Stock','Min Level','Status','Expiry','Actions'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} style={{ ...S.td, textAlign:'center', color:'#9ca3af', padding:28 }}>No items found.</td></tr>
            )}
            {filtered.map((item, idx) => {
              const rowStyle = getRowStyle(item);
              const isExpired = daysUntil(item.expiry) < 0;
              const tdStyle = { ...S.td, ...rowStyle };
              return (
                <tr key={item.id} style={rowStyle}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={{ ...tdStyle, fontWeight:600 }}>{item.name}</td>
                  <td style={tdStyle}>
                    <span className="emr-chip" style={{ fontSize:11 }}>{item.category}</span>
                  </td>
                  <td style={tdStyle}>{item.unit}</td>
                  <td style={{ ...tdStyle, fontWeight:700 }}>
                    <input type="number" min={0} value={item.current} onChange={(e) => updateQty(item.id, e.target.value)} style={{ width: 70, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4 }} />
                  </td>
                  <td style={tdStyle}>{item.min}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={item.status} expiry={item.expiry} />
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: daysUntil(item.expiry) <= 30 ? (isExpired ? '#fff' : '#991b1b') : 'inherit', fontWeight: daysUntil(item.expiry) <= 30 ? 600 : 400 }}>
                      {formatDate(item.expiry)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button type="button" className="btn-sm" style={S.smBtnBlue} onClick={() => setViewItem(item)}>View</button>
                    {' '}
                    <button type="button" className="btn-sm" style={S.smBtn} onClick={() => setEditItem(item)}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {onSaveStock && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => setEditItem(EMPTY_ITEM())}>+ Add Item</button>
          <button type="button" className="btn btn-primary" style={S.saveBtn} disabled={saving || itemBusy} onClick={handleSaveLevels}>
            {saving ? 'Saving…' : '✓ Save Stock Levels'}
          </button>
        </div>
      )}
      <p className="hint" style={{ marginTop:10, fontSize:12, color:'#9ca3af' }}>
        Showing {filtered.length} of {items.length} items · Live central store inventory
      </p>

      {viewItem && (
        <ItemViewModal
          item={viewItem}
          onClose={() => setViewItem(null)}
          onEdit={(item) => setEditItem(item)}
        />
      )}
      {editItem && (
        <ItemFormModal
          item={editItem}
          title={items.some((i) => i.id === editItem.id) ? 'Edit Inventory Item' : 'Add Inventory Item'}
          busy={itemBusy}
          error={itemErr}
          onClose={() => { setEditItem(null); setItemErr(''); }}
          onSave={saveNewOrEditedItem}
        />
      )}
    </div>
  );
}

// ─── Tab: GRN ─────────────────────────────────────────────────────────────────

const EMPTY_GRN_ITEM = () => ({
  _key: Date.now() + Math.random(),
  name:'', unit:'', qty:'', rate:'', mrp:'', batch:'', mfgDate:'', expiry:'',
});

function GRNTab({ grnHistory, onSaveGrn, stockItems }) {
  const [form, setForm] = useState({
    supplier:'', invoice:'', invoiceDate:'', poNo:'', receivedBy:'', remarks:'',
  });
  const [rows, setRows] = useState([EMPTY_GRN_ITEM()]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateRow = (key, field, val) => {
    setRows(r => r.map(row => row._key === key ? { ...row, [field]: val } : row));
  };

  const addRow = () => setRows(r => [...r, EMPTY_GRN_ITEM()]);

  const removeRow = (key) => setRows(r => r.filter(row => row._key !== key));

  const total = useMemo(() => {
    return rows.reduce((sum, row) => {
      const qty  = parseFloat(row.qty)  || 0;
      const rate = parseFloat(row.rate) || 0;
      return sum + qty * rate;
    }, 0);
  }, [rows]);

  const handleSave = async () => {
    if (!onSaveGrn) return;
    if (!form.supplier?.trim() || !form.invoice?.trim()) {
      setSaveErr('Supplier name and invoice number are required.');
      return;
    }
    const validRows = rows.filter((r) => r.name?.trim() && Number(r.qty) > 0);
    if (!validRows.length) {
      setSaveErr('Add at least one item with name and quantity.');
      return;
    }
    setBusy(true);
    setSaveErr('');
    try {
      await onSaveGrn({
        id: `GRN-${Date.now()}`,
        ...form,
        items: validRows.map(({ _key, ...r }) => r),
        total,
        savedAt: new Date().toISOString(),
      });
      setSaved(true);
      setForm({ supplier:'', invoice:'', invoiceDate:'', poNo:'', receivedBy:'', remarks:'' });
      setRows([EMPTY_GRN_ITEM()]);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.message || 'Failed to save GRN');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="info-banner" style={S.infoBanner}>
        <strong>Goods Receipt Note (GRN)</strong> — Record items received from suppliers against Purchase Orders.
      </div>

      {saved && (
        <div style={{ ...S.infoBanner, background:'#dcfce7', border:'1px solid #86efac', color:'#166534' }}>
          ✅ GRN saved — stock levels updated.
        </div>
      )}
      {saveErr && <div className="error mb">{saveErr}</div>}

      {/* Header Fields */}
      <p style={S.sectionTitle}>GRN Header</p>
      <div style={S.formGrid}>
        {[
          { label:'Supplier Name *', key:'supplier',     type:'text',  placeholder:'e.g. MedLine Supplies' },
          { label:'Invoice No *',    key:'invoice',      type:'text',  placeholder:'e.g. INV-2024-001' },
          { label:'Invoice Date *',  key:'invoiceDate',  type:'date',  placeholder:'' },
          { label:'PO No (Ref)',      key:'poNo',         type:'text',  placeholder:'e.g. PO-2024-001' },
          { label:'Received By *',   key:'receivedBy',   type:'text',  placeholder:'Staff name' },
          { label:'Remarks',         key:'remarks',      type:'text',  placeholder:'Optional notes' },
        ].map(f => (
          <div key={f.key} className="field">
            <label style={S.label}>{f.label}</label>
            <input
              className="input"
              style={S.inputField}
              type={f.type}
              placeholder={f.placeholder}
              value={form[f.key]}
              onChange={e => updateForm(f.key, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* Item Lines */}
      <p style={S.sectionTitle}>Item Lines</p>
      <div style={{ overflowX:'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Item Name','Unit','Qty Received','Rate (₹)','MRP (₹)','Batch No','Mfg Date','Expiry Date',''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._key}>
                <td style={S.td}><input style={{ ...S.inputField, minWidth:160 }} placeholder="Item name" value={row.name}    onChange={e => updateRow(row._key,'name',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:70 }}  placeholder="Unit"     value={row.unit}    onChange={e => updateRow(row._key,'unit',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:80 }}  type="number" min={0} placeholder="0"    value={row.qty}     onChange={e => updateRow(row._key,'qty',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:90 }}  type="number" min={0} placeholder="0.00" value={row.rate}    onChange={e => updateRow(row._key,'rate',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:90 }}  type="number" min={0} placeholder="0.00" value={row.mrp}     onChange={e => updateRow(row._key,'mrp',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:110 }} placeholder="Batch"    value={row.batch}   onChange={e => updateRow(row._key,'batch',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:130 }} type="date"            value={row.mfgDate} onChange={e => updateRow(row._key,'mfgDate',e.target.value)} /></td>
                <td style={S.td}><input style={{ ...S.inputField, width:130 }} type="date"            value={row.expiry}  onChange={e => updateRow(row._key,'expiry',e.target.value)} /></td>
                <td style={S.td}>
                  {rows.length > 1 && (
                    <button style={S.removeBtn} onClick={() => removeRow(row._key)} title="Remove row">×</button>
                  )}
                </td>
              </tr>
            ))}
            {/* Total Row */}
            <tr style={S.totalRow}>
              <td style={{ ...S.td, fontWeight:700 }} colSpan={2}>Total</td>
              <td style={{ ...S.td, fontWeight:700 }}>{rows.reduce((s,r) => s + (parseFloat(r.qty)||0), 0)}</td>
              <td colSpan={5} style={{ ...S.td, fontWeight:700 }}>Amount: {formatCurrency(total)}</td>
              <td style={S.td}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <button style={S.addRowBtn} onClick={addRow}>+ Add Item Row</button>

      <div>
        <button className="btn btn-primary" style={S.saveBtn} onClick={handleSave} disabled={busy}>{busy ? 'Saving…' : 'Save GRN'}</button>
        <button className="btn btn-secondary" style={S.secBtn}>Cancel</button>
      </div>

      {/* GRN History */}
      <p style={{ ...S.sectionTitle, marginTop:32 }}>Recent GRN History</p>
      {!grnHistory?.length && <p className="hint table-empty">No GRN records yet. Save a GRN above to see history here.</p>}
      {(grnHistory || []).map(grn => {
        const grnItems = grn.items || [];
        const grnTotal = grn.total ?? grnItems.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
        return (
          <div key={grn.id || grn.recordId} style={S.histCard}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={S.histTitle}>{grn.id || grn.recordId} — {grn.supplier}</div>
                <div style={S.histMeta}>
                  Invoice: {grn.invoice} &nbsp;|&nbsp; Date: {formatDate(grn.invoiceDate)} &nbsp;|&nbsp;
                  PO Ref: {grn.poNo} &nbsp;|&nbsp; Received By: {grn.receivedBy}
                </div>
                {grn.remarks && <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>Note: {grn.remarks}</div>}
              </div>
              <div style={{ fontWeight:700, color:'#166534', fontSize:15 }}>{formatCurrency(grnTotal)}</div>
            </div>
            {grnItems.length > 0 && (
            <table style={{ ...S.table, marginTop:4 }}>
              <thead>
                <tr>
                  {['Item','Unit','Qty','Rate','MRP','Batch','Expiry'].map(h => <th key={h} style={{ ...S.th, fontSize:11 }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {grnItems.map((it, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontSize:12 }}>{it.name}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{it.unit}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{it.qty}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{formatCurrency(it.rate)}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{formatCurrency(it.mrp)}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{it.batch}</td>
                    <td style={{ ...S.td, fontSize:12 }}>{formatDate(it.expiry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Indent / Issue ───────────────────────────────────────────────────────

const EMPTY_INDENT_ITEM = () => ({
  _key: Date.now() + Math.random(),
  name:'', unit:'', requested:'', issued:'',
});

function IndentTab({ indentHistory, onSaveIndent, stockItems }) {
  const [form, setForm] = useState({
    from:'Central Store', to:'ICU', issueDate:'', approvedBy:'', remarks:'',
  });
  const [rows, setRows] = useState([EMPTY_INDENT_ITEM()]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const updateForm = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateRow = (key, field, val) => {
    setRows(r => r.map(row => {
      if (row._key !== key) return row;
      const updated = { ...row, [field]: val };
      if (field === 'name') {
        const found = (stockItems || []).find(s => s.name === val);
        if (found) updated.unit = found.unit;
      }
      return updated;
    }));
  };

  const addRow = () => setRows(r => [...r, EMPTY_INDENT_ITEM()]);
  const removeRow = (key) => setRows(r => r.filter(row => row._key !== key));

  const handleIssue = async () => {
    if (!onSaveIndent) return;
    const validRows = rows.filter((r) => r.name?.trim() && (Number(r.issued) > 0 || Number(r.requested) > 0));
    if (!validRows.length) {
      setSaveErr('Add at least one item with issued quantity.');
      return;
    }
    setBusy(true);
    setSaveErr('');
    try {
      await onSaveIndent({
        id: `IN-${Date.now()}`,
        ...form,
        items: validRows.map(({ _key, ...r }) => r),
        savedAt: new Date().toISOString(),
      });
      setSaved(true);
      setRows([EMPTY_INDENT_ITEM()]);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.message || 'Failed to issue stock');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="info-banner" style={S.infoBanner}>
        <strong>Indent / Issue Stock</strong> — Issue items from Central Store to departments.
      </div>

      {saved && (
        <div style={{ ...S.infoBanner, background:'#dcfce7', border:'1px solid #86efac', color:'#166534' }}>
          ✅ Stock issued — central store quantities updated.
        </div>
      )}
      {saveErr && <div className="error mb">{saveErr}</div>}

      {/* Header */}
      <p style={S.sectionTitle}>Issue Header</p>
      <div style={S.formGrid}>
        <div className="field">
          <label style={S.label}>From (Store)</label>
          <input className="input" style={{ ...S.inputField, background:'#f3f4f6' }} value="Central Store" readOnly />
        </div>
        <div className="field">
          <label style={S.label}>To Department *</label>
          <select
            className="input"
            style={S.inputField}
            value={form.to}
            onChange={e => updateForm('to', e.target.value)}
          >
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="field">
          <label style={S.label}>Issue Date *</label>
          <input className="input" style={S.inputField} type="date" value={form.issueDate} onChange={e => updateForm('issueDate', e.target.value)} />
        </div>
        <div className="field">
          <label style={S.label}>Approved By</label>
          <input className="input" style={S.inputField} placeholder="Doctor / HOD name" value={form.approvedBy} onChange={e => updateForm('approvedBy', e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn:'span 2' }}>
          <label style={S.label}>Purpose / Remarks</label>
          <input className="input" style={S.inputField} placeholder="Purpose of indent" value={form.remarks} onChange={e => updateForm('remarks', e.target.value)} />
        </div>
      </div>

      {/* Item Lines */}
      <p style={S.sectionTitle}>Item Lines</p>
      <div style={{ overflowX:'auto' }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Item Name','Unit','Qty Requested','Qty Issued',''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row._key}>
                <td style={S.td}>
                  <select
                    style={{ ...S.inputField, minWidth:220 }}
                    value={row.name}
                    onChange={e => updateRow(row._key, 'name', e.target.value)}
                  >
                    <option value="">— Select Item —</option>
                    {(stockItems || []).map(s => (
                      <option key={s.id} value={s.name}>{s.name} ({s.current} {s.unit} avail.)</option>
                    ))}
                  </select>
                </td>
                <td style={S.td}>
                  <input style={{ ...S.inputField, width:80, background:'#f3f4f6' }} value={row.unit} readOnly placeholder="Auto" />
                </td>
                <td style={S.td}>
                  <input style={{ ...S.inputField, width:90 }} type="number" min={0} placeholder="0" value={row.requested} onChange={e => updateRow(row._key,'requested',e.target.value)} />
                </td>
                <td style={S.td}>
                  <input style={{ ...S.inputField, width:90 }} type="number" min={0} placeholder="0" value={row.issued} onChange={e => updateRow(row._key,'issued',e.target.value)} />
                </td>
                <td style={S.td}>
                  {rows.length > 1 && (
                    <button style={S.removeBtn} onClick={() => removeRow(row._key)} title="Remove">×</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button style={S.addRowBtn} onClick={addRow}>+ Add Item Row</button>

      <div>
        <button className="btn btn-primary" style={S.saveBtn} onClick={handleIssue} disabled={busy}>{busy ? 'Saving…' : 'Issue Stock'}</button>
        <button className="btn btn-secondary" style={S.secBtn}>Cancel</button>
      </div>

      {/* History */}
      <p style={{ ...S.sectionTitle, marginTop:32 }}>Recent Indent History</p>
      {!indentHistory?.length && <p className="hint table-empty">No indent records yet. Issue stock above to see history here.</p>}
      {(indentHistory || []).map(ind => (
        <div key={ind.id || ind.recordId} style={S.histCard}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={S.histTitle}>{ind.id} — {ind.from} → {ind.to}</div>
              <div style={S.histMeta}>
                Date: {formatDate(ind.issueDate)} &nbsp;|&nbsp; Approved By: {ind.approvedBy}
              </div>
              {ind.remarks && <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>Remarks: {ind.remarks}</div>}
            </div>
            <span className="emr-chip" style={{ fontSize:11, background:'#dcfce7', color:'#166534' }}>Issued</span>
          </div>
          <table style={{ ...S.table, marginTop:6 }}>
            <thead>
              <tr>
                {['Item','Unit','Requested','Issued'].map(h => <th key={h} style={{ ...S.th, fontSize:11 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {ind.items.map((it, i) => (
                <tr key={i}>
                  <td style={{ ...S.td, fontSize:12 }}>{it.name}</td>
                  <td style={{ ...S.td, fontSize:12 }}>{it.unit}</td>
                  <td style={{ ...S.td, fontSize:12 }}>{it.requested}</td>
                  <td style={{ ...S.td, fontSize:12, fontWeight:600 }}>{it.issued}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Purchase Orders ──────────────────────────────────────────────────────

function PurchaseOrdersTab({ poList, onCreatePo, onPatchPo }) {
  const [selectedPO, setSelectedPO] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ poNo: '', supplier: '', date: '', items: 1, value: '', status: 'Pending' });
  const [busyId, setBusyId] = useState('');
  const pos = poList || [];

  const approvePo = async (po) => {
    const id = po.recordId || po.id;
    if (!id || !onPatchPo) return;
    if (!window.confirm(`Approve ${po.poNo}?`)) return;
    setBusyId(id);
    try {
      await onPatchPo(id, { status: 'Approved', approvedAt: new Date().toISOString() });
    } catch (err) {
      window.alert(err.message || 'Could not approve PO');
    } finally {
      setBusyId('');
    }
  };

  const markReceived = async (po) => {
    const id = po.recordId || po.id;
    if (!id || !onPatchPo) return;
    if (po.status !== 'Approved') {
      window.alert('Approve the PO before marking received.');
      return;
    }
    setBusyId(id);
    try {
      await onPatchPo(id, { status: 'Received', receivedAt: new Date().toISOString() });
    } catch (err) {
      window.alert(err.message || 'Could not update PO');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div>
      <div className="info-banner" style={S.infoBanner}>
        <strong>Purchase Orders</strong> — Track POs raised with suppliers.
      </div>

      {selectedPO && (
        <div style={{ ...S.histCard, border:'1px solid #bfdbfe', marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <div style={{ ...S.histTitle, fontSize:15 }}>{selectedPO.poNo} — Details</div>
            <button style={S.smBtn} onClick={() => setSelectedPO(null)}>✕ Close</button>
          </div>
          <div style={S.histMeta}>
            Supplier: <strong>{selectedPO.supplier}</strong> &nbsp;|&nbsp;
            Date: {formatDate(selectedPO.date)} &nbsp;|&nbsp;
            Items: {selectedPO.items} &nbsp;|&nbsp;
            Value: <strong>{formatCurrency(selectedPO.value)}</strong> &nbsp;|&nbsp;
            Status: <POStatusBadge status={selectedPO.status} />
          </div>
          <div className="info-banner" style={{ ...S.infoBanner, marginTop:8, marginBottom:0, fontSize:12 }}>
            Full PO line items are managed in the procurement module. This view shows summary details only.
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ ...S.statGrid, gridTemplateColumns:'repeat(3,1fr)', marginBottom:20 }}>
        {[
          { label:'Pending Orders',  val: pos.filter(p=>p.status==='Pending').length,  color:'#854d0e' },
          { label:'Approved Orders', val: pos.filter(p=>p.status==='Approved').length, color:'#1e40af' },
          { label:'Received Orders', val: pos.filter(p=>p.status==='Received').length, color:'#166534' },
        ].map(s => (
          <div key={s.label} style={S.statCard}>
            <p style={{ ...S.statValue, color: s.color }}>{s.val}</p>
            <p style={S.statLabel}>{s.label}</p>
          </div>
        ))}
      </div>

      <div className="card" style={{ overflowX:'auto', padding:0 }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['PO No','Supplier','Date','Items','Value','Status','Actions'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pos.map(po => (
              <tr key={po.poNo || po.recordId}>
                <td style={{ ...S.td, fontWeight:700, color:'#2563eb' }}>{po.poNo}</td>
                <td style={S.td}>{po.supplier}</td>
                <td style={S.td}>{formatDate(po.date)}</td>
                <td style={S.td}>{po.items} items</td>
                <td style={{ ...S.td, fontWeight:600 }}>{formatCurrency(po.value)}</td>
                <td style={S.td}><POStatusBadge status={po.status} /></td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      className="btn-sm"
                      style={S.smBtnBlue}
                      onClick={() => setSelectedPO(po)}
                    >
                      View Details
                    </button>
                    {po.status === 'Pending' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busyId === (po.recordId || po.id)}
                        onClick={() => approvePo(po)}
                      >
                        Approve
                      </button>
                    )}
                    {po.status === 'Approved' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        disabled={busyId === (po.recordId || po.id)}
                        onClick={() => markReceived(po)}
                      >
                        Mark Received
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
        <button type="button" className="btn btn-primary" style={S.saveBtn} onClick={() => setShowForm((s) => !s)}>+ Create New PO</button>
      </div>
      {showForm && (
        <form className="card" style={{ marginTop: 16 }} onSubmit={async (e) => {
          e.preventDefault();
          if (!onCreatePo) return;
          try {
            await onCreatePo({ ...form, poNo: form.poNo || `PO-${Date.now()}`, items: Number(form.items) || 1, value: Number(form.value) || 0, date: form.date || new Date().toISOString().slice(0, 10), status: 'Pending' });
            setShowForm(false);
            setForm({ poNo: '', supplier: '', date: '', items: 1, value: '', status: 'Pending' });
          } catch (err) {
            window.alert(err.message || 'Could not create purchase order');
          }
        }}>
          <div className="form-grid mb">
            <div className="field"><label>Supplier</label><input className="input" required value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} /></div>
            <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div className="field"><label>Items</label><input type="number" className="input" value={form.items} onChange={(e) => setForm((f) => ({ ...f, items: e.target.value }))} /></div>
            <div className="field"><label>Value (₹)</label><input type="number" className="input" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} /></div>
          </div>
          <button type="submit" className="btn btn-primary btn-sm">Save PO</button>
        </form>
      )}
    </div>
  );
}

function ReportsTab({ stockItems }) {
  const source = stockItems || [];
  const [period, setPeriod] = useState(30);

  const expiryItems = useMemo(() => {
    return source
      .map(item => ({ ...item, daysLeft: daysUntil(item.expiry) }))
      .filter(item => item.daysLeft >= 0 && item.daysLeft <= period)
      .sort((a, b) => a.daysLeft - b.daysLeft);
  }, [period, source]);

  const valuationByCategory = useMemo(() => {
    const map = {};
    source.forEach(item => {
      if (!map[item.category]) {
        map[item.category] = { category: item.category, totalItems: 0, totalQty: 0, estValue: 0 };
      }
      map[item.category].totalItems += 1;
      map[item.category].totalQty   += item.current;
      map[item.category].estValue   += item.current * item.mrp;
    });
    return Object.values(map).sort((a, b) => b.estValue - a.estValue);
  }, [source]);

  const grandTotal = useMemo(() => ({
    items : valuationByCategory.reduce((s, c) => s + c.totalItems, 0),
    qty   : valuationByCategory.reduce((s, c) => s + c.totalQty,   0),
    value : valuationByCategory.reduce((s, c) => s + c.estValue,   0),
  }), [valuationByCategory]);

  return (
    <div>
      {/* Expiry Alert Section */}
      <p style={S.sectionTitle}>Expiry Alerts</p>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
        <span style={{ fontSize:13, fontWeight:500, color:'#374151' }}>Expiring within:</span>
        <div className="period-tabs" style={{ display:'flex', gap:6 }}>
          {[30, 60, 90].map(p => (
            <button
              key={p}
              style={{ ...S.periodBtn, ...(period === p ? S.periodBtnA : {}) }}
              onClick={() => setPeriod(p)}
            >
              {p} days
            </button>
          ))}
        </div>
      </div>

      {expiryItems.length === 0 ? (
        <div style={{ ...S.infoBanner, background:'#dcfce7', border:'1px solid #86efac', color:'#166534' }}>
          ✅ No items expiring within {period} days.
        </div>
      ) : (
        <div className="card" style={{ overflowX:'auto', padding:0, marginBottom:28 }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['#','Item Name','Category','Batch','Expiry Date','Days Remaining','Current Stock','Unit'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expiryItems.map((item, idx) => {
                const urgent = item.daysLeft <= 30;
                const rowBg  = urgent ? { background:'#fee2e2' } : { background:'#fef9c3' };
                return (
                  <tr key={item.id} style={rowBg}>
                    <td style={{ ...S.td }}>{idx + 1}</td>
                    <td style={{ ...S.td, fontWeight:600 }}>{item.name}</td>
                    <td style={S.td}><span className="emr-chip" style={{ fontSize:11 }}>{item.category}</span></td>
                    <td style={S.td}>{item.batch}</td>
                    <td style={{ ...S.td, fontWeight:600, color: urgent ? '#991b1b' : '#854d0e' }}>
                      {formatDate(item.expiry)}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        fontWeight:700,
                        color: item.daysLeft <= 15 ? '#991b1b' : item.daysLeft <= 30 ? '#b45309' : '#854d0e'
                      }}>
                        {item.daysLeft}d
                      </span>
                    </td>
                    <td style={{ ...S.td, fontWeight:600 }}>{item.current}</td>
                    <td style={S.td}>{item.unit}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock Valuation Section */}
      <p style={S.sectionTitle}>Stock Valuation by Category</p>
      <div className="card" style={{ overflowX:'auto', padding:0 }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Category','Total Items','Total Qty','Est. Value (₹)'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {valuationByCategory.map(row => (
              <tr key={row.category}>
                <td style={S.td}>
                  <span className="emr-chip" style={{ fontSize:11 }}>{row.category}</span>
                </td>
                <td style={S.td}>{row.totalItems}</td>
                <td style={S.td}>{row.totalQty.toLocaleString('en-IN')}</td>
                <td style={{ ...S.td, fontWeight:600, color:'#166534' }}>{formatCurrency(row.estValue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={S.tfoot}>
              <td style={{ ...S.td, fontWeight:700 }}>Grand Total</td>
              <td style={{ ...S.td, fontWeight:700 }}>{grandTotal.items}</td>
              <td style={{ ...S.td, fontWeight:700 }}>{grandTotal.qty.toLocaleString('en-IN')}</td>
              <td style={{ ...S.td, fontWeight:700, color:'#166534', fontSize:15 }}>{formatCurrency(grandTotal.value)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="hint" style={{ marginTop:10, fontSize:12, color:'#9ca3af' }}>
        Estimated value = Current Stock × MRP per item. Reference date: {TODAY.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.
      </p>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

const TABS = [
  { key:'stock',  label:'Stock Overview'   },
  { key:'grn',    label:'GRN'              },
  { key:'indent', label:'Indent / Issue'   },
  { key:'po',     label:'Purchase Orders'  },
  { key:'reports',label:'Reports'          },
];

export default function InventoryPage() {
  const [activeTab, setActiveTab] = useState('stock');
  const addItemRef = useRef(null);
  const { config, save: saveConfig, loading: configLoading, error: configError } = useHmsConfig('store-inventory');
  const { records: grnRecords, create: createGrn } = useHmsRecords('store-grn');
  const { records: indentRecords, create: createIndent } = useHmsRecords('store-indent');
  const { records: poRecords, create: createPo, patch: patchPo } = useHmsRecords('store-po');
  const [stockItems, setStockItems] = useState([]);
  const [savingStock, setSavingStock] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (config?.items?.length) setStockItems(config.items);
    else if (!configLoading) setStockItems([]);
  }, [config, configLoading]);

  const saveStock = async (items) => {
    setSavingStock(true);
    setSaveError('');
    try {
      await saveConfig({ items: items.map(normalizeItem) });
      setStockItems(items.map(normalizeItem));
    } catch (e) {
      setSaveError(e.message || 'Could not save inventory');
      throw e;
    } finally { setSavingStock(false); }
  };

  const handleSaveGrn = async (grn) => {
    await createGrn(grn);
    const updated = applyGrnToStock(stockItems, grn.items || []);
    await saveStock(updated);
  };

  const handleSaveIndent = async (indent) => {
    const { items: updated, errors } = applyIndentToStock(stockItems, indent.items || []);
    if (errors.length) throw new Error(errors.join('; '));
    await createIndent(indent);
    await saveStock(updated);
    // Issuing to Pharmacy: also top up the pharmacy inventory so both stores stay in sync
    if (String(indent.to || '').toLowerCase() === 'pharmacy') {
      const issued = (indent.items || []).filter((it) => Number(it.issued) > 0 && it.name);
      const syncFails = [];
      for (const it of issued) {
        try {
          const inv = await ipApi.getPharmacyInventory(it.name);
          const match = (inv?.items || []).find((m) => (m.name || '').toLowerCase() === it.name.toLowerCase());
          if (match) {
            await ipApi.updateInventoryItem(match.itemId, { addStock: Number(it.issued) });
          } else {
            await ipApi.createInventoryItem({ name: it.name, unit: it.unit || 'nos', stockQty: Number(it.issued), category: 'Store Issue' });
          }
        } catch (e) {
          syncFails.push(`${it.name}: ${e.message || 'sync failed'}`);
        }
      }
      if (syncFails.length) {
        setSaveError(`Store updated, but pharmacy sync failed for: ${syncFails.join('; ')}`);
      }
    }
  };

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.title}>Inventory Management</h1>
          <p style={S.subtitle}>Central Store · Live stock · GRN · Indent · Purchase orders</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button type="button" className="btn btn-secondary" style={S.secBtn} onClick={() => exportStockCsv(stockItems)} disabled={!stockItems.length}>Export</button>
          <button type="button" className="btn btn-primary" style={{ ...S.saveBtn, marginTop:0 }} onClick={() => { setActiveTab('stock'); addItemRef.current?.(); }}>+ Add Item</button>
        </div>
      </div>

      {(configError || saveError) && activeTab !== 'stock' && (
        <div className="error mb">{configError || saveError}</div>
      )}

      <div style={S.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            style={{ ...S.tab, ...(activeTab === tab.key ? S.tabActive : {}) }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'stock'   && <StockOverviewTab stockItems={stockItems} onSaveStock={saveStock} saving={savingStock} saveError={configError || saveError} onClearError={() => setSaveError('')} onRequestAdd={addItemRef} />}
      {activeTab === 'grn'     && <GRNTab grnHistory={grnRecords} onSaveGrn={handleSaveGrn} stockItems={stockItems} />}
      {activeTab === 'indent'  && <IndentTab indentHistory={indentRecords} onSaveIndent={handleSaveIndent} stockItems={stockItems} />}
      {activeTab === 'po'      && <PurchaseOrdersTab poList={poRecords} onCreatePo={createPo} onPatchPo={patchPo} />}
      {activeTab === 'reports' && <ReportsTab stockItems={stockItems} />}
    </div>
  );
}
